import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Fab from '@mui/material/Fab';
import Typography from '@mui/material/Typography';
import {
  GridActionsCellItem,
  GridActionsCellItemProps,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
} from '@mui/x-data-grid-premium';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { GridActionsCellItemLink } from '../../components/FakeLink';
import { Table } from '../../components/Table';
import { TableRef } from '../../components/Table/Table';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { useSearchParamsState } from '../../hooks';
import { fetchPromoCodes } from '../../redux/modules/prompCodes/promoCode.actions';
import { promoCodeSelector } from '../../redux/modules/prompCodes/promoCode.selectors';
import { locations } from '../../routes/locations';
import { PromoCode, PromoCodeTypesEnum } from '../../types/promoCodes';
import { parseJSON } from '../../utils/json';
import { PromoCodeDialog } from './PromoCodeDialog';

export function PromoCodesList() {
  const tableRef = useRef<TableRef>(null);
  const [open, setOpen] = useState(false);
  const [editValue, setEditValue] = useState<PromoCode | null>(null);
  const [columns, setColumns] = useState<GridColDef[]>([
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
    },
    {
      field: 'code',
      headerName: 'Code',
      flex: 1,
    },
    {
      field: 'maxUses',
      headerName: 'Max uses',
      flex: 1,
    },
    {
      field: 'currentUses',
      headerName: 'Total uses',
      flex: 1,
    },
    {
      field: 'isActive',
      headerName: 'Status',
      flex: 1,
      valueFormatter: (params) => (params?.value ? 'Active' : 'Expired'),
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 120,
      valueFormatter: (params) => PromoCodeTypesEnum[params?.value],
    },
    {
      field: 'expirationDate',
      headerName: 'Expiration Date',
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return '';
        }
        return params.formattedValue;
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      type: 'date',
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return '';
        }
        return params.formattedValue;
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      type: 'date',
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return '';
        }
        return params.formattedValue;
      },
    },
  ]);
  const [initialFilterModel, setInitialFilterModel] = useSearchParamsState('filter', '');
  const [initialPage, setInitialPage] = useSearchParamsState('page', '0');
  const [initialPageSize, setInitialPageSize] = useSearchParamsState('pageSize', '100');

  const { totalPromoCodes, promoCodes, fetchStatus } = useSelector(promoCodeSelector);
  const isLoading = fetchStatus === fetchStatuses.pending;

  const handleClose = (isReload: boolean) => {
    if (isReload) {
      tableRef?.current?.reload();
    }

    setEditValue(null);
    setOpen(false);
  };

  const onEditPromoCode = (row: PromoCode): void => {
    setEditValue(row);
    setOpen(true);
  };

  const onAddNew = (): void => {
    setOpen(true);
  };

  useEffect(() => {
    const actionColumn = {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      hideable: false,
      filterable: false,
      sortable: false,
      getActions: (params: GridRowParams) => {
        const actions: React.ReactElement<GridActionsCellItemProps>[] = [];
        const expirationDate = params.row?.expirationDate;
        const today = new Date().toISOString().split('T')[0];
        const isExpired = expirationDate && today > expirationDate;
        actions.push(
          <GridActionsCellItemLink
            key={`employee-list-read-${params.row.id}`}
            icon={<OpenInNewIcon />}
            label="Link"
            href={locations.promoCodeUsages(params.row.id)}
          />,
        );

        actions.push(
          <GridActionsCellItem
            key={`promoCode-list-update-${params.row.id}`}
            icon={<EditOutlinedIcon />}
            label="Actions"
            onClick={() => onEditPromoCode(params.row)}
            disabled={params?.row?.maxUses === params.row.currentUses || isExpired}
          />,
        );

        return actions;
      },
    };

    setColumns([actionColumn, ...columns.filter((i) => i.type !== 'actions')]);
  }, []);

  return (
    <Container component="main" maxWidth="xl">
      <Box sx={{ pb: 5 }}>
        <Typography variant="h4">
          Promo Codes &nbsp;
          <Fab color="primary" size="small" aria-label="Create Promo Code" onClick={onAddNew}>
            <AddIcon />
          </Fab>
        </Typography>
      </Box>

      <PromoCodeDialog open={open} onClose={handleClose} initialValue={editValue} />

      <Table
        ref={tableRef}
        fetchItems={fetchPromoCodes}
        rows={promoCodes}
        columns={columns}
        loading={isLoading}
        rowCount={totalPromoCodes}
        initialFilterModel={parseJSON(initialFilterModel)}
        initialPage={+initialPage}
        initialPageSize={+initialPageSize}
        onFilterModelChange={(filterModel) => setInitialFilterModel(JSON.stringify(filterModel))}
        onPageChange={(page) => setInitialPage(`${page}`)}
        onPageSizeChange={(pageSize) => setInitialPageSize(`${pageSize}`)}
      />
    </Container>
  );
}
